import { useEffect, useRef } from 'react';
import { animate, useInView } from 'framer-motion';
import { EASE_OUT } from 'shared/components/motion/tokens';

interface AnimatedNumberProps {
  /** Valor já formatado em pt-BR: "R$ 19.400", "68%", "R$ 79,80", "2,4 / mês"… */
  value: string;
  className?: string;
}

// Primeiro número pt-BR do texto (milhar com ".", decimal com ",").
const NUMBER_PATTERN = /-?\d{1,3}(?:\.\d{3})+(?:,\d+)?|-?\d+(?:,\d+)?/;

function parse(value: string) {
  const match = value.match(NUMBER_PATTERN);
  if (!match || match.index === undefined) return null;
  const raw = match[0];
  return {
    prefix: value.slice(0, match.index),
    suffix: value.slice(match.index + raw.length),
    number: Number(raw.replace(/\./g, '').replace(',', '.')),
    decimals: raw.includes(',') ? raw.split(',')[1].length : 0,
  };
}

// Conta de 0 até o valor quando entra na tela, e do valor antigo ao novo quando muda
// (ex.: troca de período). Mantém prefixo/sufixo ("R$ ", "%", " h") intactos.
export function AnimatedNumber({ value, className }: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const current = useRef(0);
  const inView = useInView(ref, { once: true });
  const parsed = parse(value);

  const prefix = parsed?.prefix ?? '';
  const suffix = parsed?.suffix ?? '';
  const decimals = parsed?.decimals ?? 0;
  const target = parsed?.number ?? 0;

  useEffect(() => {
    if (!inView || !parsed) return;
    const format = (latest: number) =>
      `${prefix}${latest.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;

    const controls = animate(current.current, target, {
      duration: 1.1,
      ease: EASE_OUT,
      onUpdate: (latest) => {
        current.current = latest;
        if (ref.current) ref.current.textContent = format(latest);
      },
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, target, prefix, suffix, decimals]);

  return (
    <span ref={ref} className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {parsed ? `${prefix}${(0).toLocaleString('pt-BR', { minimumFractionDigits: decimals })}${suffix}` : value}
    </span>
  );
}
